import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

/** api.get already returns the JSON body `{ success, data, pagination }`. Do not peel `.data` again. */
function unwrapPage<T>(res: unknown): {
  success: boolean;
  data: T[];
  pagination: { page: number; pageSize: number; total: number };
} {
  const r = res as {
    success?: boolean;
    data?: unknown;
    pagination?: { page: number; pageSize: number; total: number };
  };
  if (r && Array.isArray(r.data) && r.pagination) {
    return {
      success: r.success !== false,
      data: r.data as T[],
      pagination: r.pagination,
    };
  }
  // Defensive: axios-shaped or already-peeled array
  const nested = r?.data as typeof r | undefined;
  if (nested && Array.isArray(nested.data) && nested.pagination) {
    return {
      success: nested.success !== false,
      data: nested.data as T[],
      pagination: nested.pagination,
    };
  }
  if (Array.isArray(r)) {
    return {
      success: true,
      data: r as unknown as T[],
      pagination: { page: 1, pageSize: r.length, total: r.length },
    };
  }
  return {
    success: true,
    data: [],
    pagination: { page: 1, pageSize: 50, total: 0 },
  };
}

export interface PmvRuntime {
  disabled: boolean;
  connectedFleetDisabled?: boolean;
  schemaVersion?: string;
  featureKey?: string;
  connectedFeatureKey?: string;
}

export interface PmvConnectedSummary {
  connected_units: number;
  online: number;
  delayed: number;
  offline: number;
  unmapped_devices: number;
  provider_issues: number;
  open_quarantine: number;
  open_reconciliation: number;
  spatial?: {
    with_location: number;
    stale_location: number;
    no_location: number;
    open_spatial_attention: number;
    location_mismatches: number;
    restricted_area: number;
    unexpected_movement: number;
  };
  attention: Array<{ key: string; label: string; count: number }>;
}

export interface PmvFleetMapMarker {
  pmv_profile_id: string;
  asset_id: string;
  fleet_number: string | null;
  asset_code: string | null;
  asset_name: string | null;
  classification_id: string;
  operational_status: string;
  connectivity: string | null;
  location: {
    lat: number;
    lon: number;
    accuracy_m: number | null;
    observed_at: string | null;
    quality: string | null;
    freshness: string;
  } | null;
  movement_state: string;
  stationary_since: string | null;
  gps_observed_distance_m: number;
  observed: {
    inside_geofence_ids: string[];
    project_ids: string[];
    site_refs: string[];
    work_front_refs: string[];
    restricted: boolean;
  };
  expected: {
    source: string;
    project_id: string | null;
    allocation_id: string | null;
  };
  attention: { open_count: number; types: string[] };
  last_spatial_event: { event_type: string; observed_at: string } | null;
  map_eligible: boolean;
}

export interface PmvFleetMapResponse {
  generated_at: string;
  summary: {
    total_connected: number;
    map_markers: number;
    list_rows: number;
    live: number;
    delayed: number;
    last_known: number;
    with_attention: number;
  };
  markers: PmvFleetMapMarker[];
  list: PmvFleetMapMarker[];
  clusters: unknown;
  authority: {
    mutates_allocation: boolean;
    mutates_ops_status: boolean;
    observed_only: boolean;
    list_fallback: boolean;
  };
}

export interface PmvMapGeofence {
  id: string;
  code: string;
  name: string;
  type: string;
  priority: number;
  project_id: string | null;
  site_ref: string | null;
  work_front_ref: string | null;
  geometry_type: string;
  center_lat: number | null;
  center_lon: number | null;
  radius_m: number | null;
  geometry_json: { type?: string; coordinates?: number[][][] } | null;
  geometry_revision: number;
}

export interface PmvFleetMapCard extends PmvFleetMapMarker {
  card: {
    title: string;
    expected_label: string;
    observed_label: string;
    last_position_label: string;
    actions: Array<{ kind: string; path: string }>;
  };
}

export interface PmvGeofence {
  id: string;
  company_id: string;
  code: string;
  name: string;
  type: string;
  status: string;
  geometry_type: string;
  center_lat: number | null;
  center_lon: number | null;
  radius_m: number | null;
  geometry_json: { type?: string; coordinates?: number[][][] } | null;
  geometry_revision: number;
  project_id: string | null;
  site_ref: string | null;
  work_front_ref: string | null;
  purpose: string | null;
  description: string | null;
  entry_monitoring_enabled: boolean;
  exit_monitoring_enabled: boolean;
  dwell_monitoring_enabled: boolean;
  movement_monitoring_enabled: boolean;
  priority: number;
  effective_from: string | null;
  effective_until: string | null;
  activated_at: string | null;
  is_active: boolean;
}

export interface PmvGeofenceCreatePayload {
  code: string;
  name: string;
  type: string;
  geometry_type: 'CIRCLE' | 'POLYGON';
  center_lat?: number;
  center_lon?: number;
  radius_m?: number;
  geometry_json?: { type: 'Polygon'; coordinates: number[][][] };
  project_id?: string | null;
  site_ref?: string | null;
  work_front_ref?: string | null;
  purpose?: string | null;
  description?: string | null;
  entry_monitoring_enabled?: boolean;
  exit_monitoring_enabled?: boolean;
  dwell_monitoring_enabled?: boolean;
  movement_monitoring_enabled?: boolean;
  priority?: number;
  effective_from?: string | null;
  effective_until?: string | null;
}

export interface PmvGeofenceRevision {
  id: string;
  geofence_id: string;
  revision: number;
  geometry_type: string;
  center_lat: number | null;
  center_lon: number | null;
  radius_m: number | null;
  geometry_json: unknown;
  change_reason: string | null;
  created_at: string;
}

export interface PmvConnectedProvider {
  id: string;
  provider_key: string;
  display_name: string;
  adapter_type: string;
  status: string;
  health_status: string;
  health_detail?: string | null;
  has_webhook_secret?: boolean;
  has_ingest_token?: boolean;
  is_paused?: boolean;
  sync_mode?: string;
  poll_interval_sec?: number | null;
  online_threshold_sec?: number;
  delayed_threshold_sec?: number;
  /** Returned once on create / rotate — never listed again */
  ingest_token?: string;
}

export interface PmvConnectedDevice {
  id: string;
  provider_id: string;
  external_device_id: string;
  device_type: string;
  status: string;
  last_seen_at?: string | null;
  serial_number?: string | null;
}

export interface PmvEquipmentConnectedState {
  connected: boolean;
  operational_status: string;
  connectivity: 'ONLINE' | 'DELAYED' | 'OFFLINE' | null;
  state: Record<string, unknown> | null;
  devices: PmvConnectedDevice[];
  assignments: unknown[];
  meter_comparison: {
    certified_engine_hours: number | null;
    telematics_engine_hours: number | null;
    delta_hours: number | null;
    authority: string;
    note: string;
  };
  contradictions: Array<{ type: string; detail: string }>;
}

export interface PmvTelemetrySignalRow {
  id: string;
  signal_code: string;
  signal_quality: string;
  value_number?: number | null;
  value_text?: string | null;
  unit?: string | null;
  lat?: number | null;
  lon?: number | null;
  accuracy_m?: number | null;
  observed_at: string;
  provenance_json?: Record<string, unknown> | null;
}

export interface PmvLocationHistoryResponse {
  points: Array<{
    id: string;
    lat: number | null;
    lon: number | null;
    accuracy_m: number | null;
    signal_quality: string;
    observed_at: string;
    device_id?: string | null;
    provider_id?: string | null;
  }>;
  meta: {
    points_original: number;
    points_returned: number;
    sampling_method: 'RAW' | 'EVEN_SAMPLE';
    from: string | null;
    to: string | null;
    note: string;
  };
}

export interface PmvSpatialEventRow {
  id: string;
  event_type: string;
  geofence_id: string | null;
  observed_at: string;
  detected_at: string;
  lat: number | null;
  lon: number | null;
  quality: string | null;
  status: string;
  evidence_json?: Record<string, unknown> | null;
  is_historical?: boolean;
}

export interface PmvMovementView {
  pmv_profile_id: string;
  movement_state: string;
  last_observed_at: string | null;
  last_speed_kmh: number | null;
  stationary_since: string | null;
  gps_observed_distance_m: number;
  gps_observed_distance_label: string;
  not_odometer: boolean;
  last_jump_at: string | null;
  last_implied_speed_kmh: number | null;
}

export interface PmvConnectedDiagnostics {
  open_quarantine: Array<{
    id: string;
    reason_code: string;
    detail?: string | null;
    external_device_id?: string | null;
    created_at: string;
  }>;
  pending_raw_events: number;
  providers: Array<{
    id: string;
    provider_key: string;
    display_name: string;
    health_status: string;
    health_detail?: string | null;
  }>;
  contradictions: Array<{ pmv_profile_id: string; type: string; detail: string }>;
  unmapped_devices: number;
}

export interface PmvProfileListItem {
  id: string;
  asset_id: string;
  asset_code: string;
  asset_name: string;
  manufacturer?: string | null;
  model?: string | null;
  operational_status: string;
  ownership_type: string;
  criticality?: string | null;
  fleet_number?: string | null;
  current_allocation_status?: string | null;
  current_project_id?: string | null;
  expected_release?: string | null;
}

export interface PmvClassification {
  id: string;
  code: string;
  name: string;
  class_family: string;
  parent_id?: string | null;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
  is_system?: boolean;
}


export interface PmvReadinessResult {
  overall: 'PASS' | 'WARNING' | 'BLOCKED';
  blockers: string[];
  warnings: string[];
  sections: Array<{
    key: string;
    status: string;
    checks: Array<{ key: string; label: string; status: string; message?: string }>;
  }>;
}

export const pmvService = {
  async runtime(): Promise<PmvRuntime> {
    const res = await api.get('/pmv/runtime');
    return unwrap<PmvRuntime>(res);
  },

  async listProfiles(params: Record<string, string | number | undefined>) {
    const res = await api.get('/pmv/profiles', { params });
    return unwrapPage<PmvProfileListItem>(res);
  },

  async getProfile(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}`);
    return unwrap<PmvProfileListItem & Record<string, unknown>>(res);
  },

  async getProfileByAsset(assetId: string) {
    const res = await api.get(`/pmv/assets/${assetId}/profile`);
    return unwrap<PmvProfileListItem | null>(res);
  },

  async activate(body: {
    asset_id: string;
    classification_id: string;
    ownership_type: string;
    fleet_number?: string | null;
    criticality?: string | null;
  }) {
    const res = await api.post('/pmv/profiles/activate', body);
    return unwrap<{ id: string }>(res);
  },

  async getWorkspace(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/workspace`);
    return unwrap<Record<string, unknown>>(res);
  },

  async getReadiness(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/readiness`);
    return unwrap<PmvReadinessResult>(res);
  },

  async listClassifications() {
    const res = await api.get('/pmv/classifications');
    return unwrap<PmvClassification[]>(res);
  },

  async createClassification(body: {
    code: string;
    name: string;
    class_family: 'PLANT' | 'MACHINERY' | 'VEHICLE';
    parent_id?: string | null;
    description?: string | null;
    sort_order?: number;
  }) {
    const res = await api.post('/pmv/classifications', body);
    return unwrap<PmvClassification>(res);
  },

  async patchClassification(
    id: string,
    body: Partial<{
      code: string;
      name: string;
      class_family: 'PLANT' | 'MACHINERY' | 'VEHICLE';
      parent_id: string | null;
      description: string | null;
      sort_order: number;
      is_active: boolean;
    }>
  ) {
    const res = await api.patch(`/pmv/classifications/${id}`, body);
    return unwrap<PmvClassification>(res);
  },

  async putSpecifications(profileId: string, values: Array<{ attribute_definition_id: string; value_text?: string | null; value_number?: number | null }>) {
    const res = await api.put(`/pmv/profiles/${profileId}/specifications`, { values });
    return unwrap<unknown>(res);
  },

  async statusTransition(profileId: string, body: { to_status: string; reason_text?: string }) {
    const res = await api.post(`/pmv/profiles/${profileId}/status-transitions`, body);
    return unwrap<unknown>(res);
  },

  async patchProfile(profileId: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/profiles/${profileId}`, body);
    return unwrap<unknown>(res);
  },

  async getOperationsSummary() {
    const res = await api.get('/pmv/operations/summary');
    return unwrap<Record<string, number>>(res);
  },

  async listRequests(params: Record<string, string | number | undefined>) {
    const res = await api.get('/pmv/requests', { params });
    return unwrapPage<PmvRequest>(res);
  },

  async getRequest(id: string) {
    const res = await api.get(`/pmv/requests/${id}`);
    return unwrap<PmvRequestDetail>(res);
  },

  async createRequest(body: Record<string, unknown>) {
    const res = await api.post('/pmv/requests', body);
    return unwrap<PmvRequestDetail>(res);
  },

  async patchRequest(id: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/requests/${id}`, body);
    return unwrap<PmvRequestDetail>(res);
  },

  async submitRequest(id: string) {
    const res = await api.post(`/pmv/requests/${id}/submit`);
    return unwrap<unknown>(res);
  },

  async getRequestMatches(id: string, requirementId?: string) {
    const res = await api.get(`/pmv/requests/${id}/matches`, {
      params: requirementId ? { requirement_id: requirementId } : undefined,
    });
    return unwrap<Array<{ requirement_id: string; matches: PmvMatchResult[] }>>(res);
  },

  async searchAvailability(params: Record<string, string | undefined>) {
    const res = await api.get('/pmv/availability', { params });
    return unwrap<PmvAvailabilityItem[]>(res);
  },

  async createReservation(body: Record<string, unknown>) {
    const res = await api.post('/pmv/reservations', body);
    return unwrap<PmvReservation>(res);
  },

  async cancelReservation(reservationId: string) {
    const res = await api.post(`/pmv/reservations/${reservationId}/cancel`, {});
    return unwrap<PmvReservation>(res);
  },

  async getAllocation(id: string) {
    const res = await api.get(`/pmv/allocations/${id}`);
    return unwrap<PmvAllocation & { dispatch_id?: string; dispatch_status?: string; dispatch_number?: string }>(res);
  },

  async createAllocation(body: Record<string, unknown>) {
    const res = await api.post('/pmv/allocations', body);
    return unwrap<PmvAllocation>(res);
  },

  async listAllocations(params: Record<string, string | number | undefined>) {
    const res = await api.get('/pmv/allocations', { params });
    return unwrapPage<PmvAllocation>(res);
  },

  async dispatchAllocation(allocationId: string, body?: Record<string, unknown>) {
    const res = await api.post(`/pmv/allocations/${allocationId}/dispatch`, body ?? {});
    return unwrap<unknown>(res);
  },

  async gateOutDispatch(dispatchId: string) {
    const res = await api.post(`/pmv/dispatches/${dispatchId}/gate-out`, {});
    return unwrap<unknown>(res);
  },

  async receiveDispatch(dispatchId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/dispatches/${dispatchId}/receive`, body);
    return unwrap<unknown>(res);
  },

  async releaseAllocation(allocationId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/allocations/${allocationId}/release`, body);
    return unwrap<unknown>(res);
  },

  async transferAllocation(
    allocationId: string,
    body: {
      to_project_id: string;
      planned_start: string;
      planned_end: string;
      to_construction_area_id?: string | null;
      to_work_front_id?: string | null;
      reason_text?: string | null;
    }
  ) {
    const res = await api.post(`/pmv/allocations/${allocationId}/transfer`, body);
    return unwrap<{ released: unknown; new_allocation: { id: string; allocation_number: string } }>(res);
  },

  async getOperationsDailySummary(operatingDate?: string) {
    const res = await api.get('/pmv/operations/daily-summary', {
      params: operatingDate ? { operating_date: operatingDate } : undefined,
    });
    return unwrap<PmvDailyOperationsSummary>(res);
  },

  async listDailyLogs(params: Record<string, string | number | undefined>) {
    const res = await api.get('/pmv/daily-logs', { params });
    return unwrapPage<PmvDailyLog>(res);
  },

  async getDailyLog(id: string, expand = true) {
    const res = await api.get(`/pmv/daily-logs/${id}`, { params: expand ? { expand: 'true' } : undefined });
    return unwrap<PmvDailyLogDetail>(res);
  },

  async createDailyLog(body: Record<string, unknown>) {
    const res = await api.post('/pmv/daily-logs', body);
    return unwrap<PmvDailyLog>(res);
  },

  async patchDailyLog(id: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/daily-logs/${id}`, body);
    return unwrap<PmvDailyLogDetail>(res);
  },

  async submitDailyLog(id: string) {
    const res = await api.post(`/pmv/daily-logs/${id}/submit`);
    return unwrap<PmvDailyLog>(res);
  },

  async certifyDailyLog(id: string) {
    const res = await api.post(`/pmv/daily-logs/${id}/certify`);
    return unwrap<PmvDailyLog>(res);
  },

  async returnDailyLog(id: string, reason: string) {
    const res = await api.post(`/pmv/daily-logs/${id}/return`, { reason });
    return unwrap<PmvDailyLog>(res);
  },

  async voidDailyLog(id: string, reason: string) {
    const res = await api.post(`/pmv/daily-logs/${id}/void`, { reason });
    return unwrap<PmvDailyLog>(res);
  },

  async createTimeEntry(dailyLogId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/daily-logs/${dailyLogId}/time-entries`, body);
    return unwrap<PmvTimeEntry>(res);
  },

  async deleteTimeEntry(dailyLogId: string, entryId: string) {
    await api.delete(`/pmv/daily-logs/${dailyLogId}/time-entries/${entryId}`);
  },

  async createOutputEntry(dailyLogId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/daily-logs/${dailyLogId}/outputs`, body);
    return unwrap<PmvOutputEntry>(res);
  },

  async getDailyLogAnalytics(id: string) {
    const res = await api.get(`/pmv/daily-logs/${id}/analytics`);
    return unwrap<PmvDailyLogAnalytics>(res);
  },

  async getProfileUtilization(profileId: string, from?: string, to?: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/utilization`, { params: { from, to } });
    return unwrap<PmvProfileUtilization>(res);
  },

  async listProfileMeters(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/meters`);
    return unwrap<PmvMeterReading[]>(res);
  },

  async listProfileInspectionTemplates(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/inspection-templates`);
    return unwrap<PmvInspectionTemplate[]>(res);
  },

  async listProfileInspections(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/inspections`);
    return unwrap<PmvInspectionResult[]>(res);
  },

  async recordProfileInspection(profileId: string, body: RecordInspectionPayload) {
    const res = await api.post(`/pmv/profiles/${profileId}/inspections`, body);
    return unwrap<{ result: PmvInspectionResult; overall: string; failAction: string }>(res);
  },

  async getInspectionResult(resultId: string) {
    const res = await api.get(`/pmv/inspection-results/${resultId}`);
    return unwrap<{ result: PmvInspectionResult; items: PmvInspectionResultItem[] }>(res);
  },

  async voidInspectionResult(resultId: string, reason: string) {
    const res = await api.post(`/pmv/inspection-results/${resultId}/void`, { reason });
    return unwrap<PmvInspectionResult>(res);
  },

  async listInspectionTemplates(classificationId?: string) {
    const res = await api.get('/pmv/inspection-templates', {
      params: classificationId ? { classification_id: classificationId } : undefined,
    });
    return unwrap<PmvInspectionTemplate[]>(res);
  },

  async createInspectionTemplate(body: Record<string, unknown>) {
    const res = await api.post('/pmv/inspection-templates', body);
    return unwrap<PmvInspectionTemplate>(res);
  },

  async listDocumentRequirements(classificationId?: string) {
    const res = await api.get('/pmv/document-requirements', {
      params: classificationId ? { classification_id: classificationId } : undefined,
    });
    return unwrap<PmvDocumentRequirement[]>(res);
  },

  async createDocumentRequirement(body: Record<string, unknown>) {
    const res = await api.post('/pmv/document-requirements', body);
    return unwrap<PmvDocumentRequirement>(res);
  },

  async listProfileBreakdowns(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/breakdowns`);
    return unwrap<PmvBreakdownEvent[]>(res);
  },

  async reportProfileBreakdown(profileId: string, body: ReportBreakdownPayload) {
    const res = await api.post(`/pmv/profiles/${profileId}/breakdowns`, body);
    return unwrap<PmvBreakdownEvent>(res);
  },

  async resolveBreakdownEvent(eventId: string, body?: { resolution_notes?: string | null }) {
    const res = await api.post(`/pmv/breakdown-events/${eventId}/resolve`, body ?? {});
    return unwrap<PmvBreakdownEvent>(res);
  },

  async escalateBreakdownEvent(eventId: string) {
    const res = await api.post(`/pmv/breakdown-events/${eventId}/escalate`);
    return unwrap<PmvBreakdownEvent>(res);
  },

  async cancelBreakdownEvent(eventId: string, body?: { resolution_notes?: string | null }) {
    const res = await api.post(`/pmv/breakdown-events/${eventId}/cancel`, body ?? {});
    return unwrap<PmvBreakdownEvent>(res);
  },

  async listProfileFuelAttributions(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/fuel-attributions`);
    return unwrap<PmvFuelAttribution[]>(res);
  },

  async recordProfileFuelAttribution(profileId: string, body: RecordFuelPayload) {
    const res = await api.post(`/pmv/profiles/${profileId}/fuel-attributions`, body);
    return unwrap<PmvFuelAttribution>(res);
  },

  async getProfileFuelSummary(profileId: string, from?: string, to?: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/fuel-summary`, {
      params: { from, to },
    });
    return unwrap<PmvFuelSummaryResponse>(res);
  },

  async listProfileOperatorAssignments(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/operator-assignments`);
    return unwrap<PmvOperatorAssignment[]>(res);
  },

  async getProfileCurrentOperator(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/operator-current`);
    return unwrap<PmvOperatorAssignment | null>(res);
  },

  async checkOperatorEligibility(profileId: string, employeeId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/operator-eligibility`, {
      params: { employee_id: employeeId },
    });
    return unwrap<OperatorEligibilityResult>(res);
  },

  async assignProfileOperator(profileId: string, body: AssignOperatorPayload) {
    const res = await api.post(`/pmv/profiles/${profileId}/operator-assignments`, body);
    return unwrap<PmvOperatorAssignment>(res);
  },

  async endOperatorAssignment(assignmentId: string, body?: { remarks?: string | null }) {
    const res = await api.post(`/pmv/operator-assignments/${assignmentId}/end`, body ?? {});
    return unwrap<PmvOperatorAssignment>(res);
  },

  async listProfileProjectCosts(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/project-costs`);
    return unwrap<PmvProjectCost[]>(res);
  },

  async getProfileCostSummary(profileId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/cost-summary`);
    return unwrap<PmvCostSummary>(res);
  },

  async getProfileBudgetCompare(profileId: string, projectId: string) {
    const res = await api.get(`/pmv/profiles/${profileId}/budget-compare`, { params: { project_id: projectId } });
    return unwrap<PmvBudgetCompare>(res);
  },

  async postFuelProjectCost(fuelAttributionId: string, body?: { cost_center_id?: string | null }) {
    const res = await api.post(`/pmv/fuel-attributions/${fuelAttributionId}/post-project-cost`, body ?? {});
    return unwrap<PmvProjectCost>(res);
  },

  // ---- PMV 2.0 Wave 4 Fuel Intelligence ----
  async getFuelControlTower() {
    const res = await api.get('/pmv/fuel/control-tower');
    return unwrap<{
      open_attention: number;
      open_reconciliation: number;
      variance_count: number;
      open_delivery_sessions: number;
      fuel_attributed_today: { count: number; litres: number | null };
      note?: string;
      wave?: string;
    }>(res);
  },

  async getFuelRegister(params?: {
    q?: string;
    operational_status?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/fuel/register', { params });
    return unwrap<{
      items: Array<{
        pmv_profile_id: string;
        fleet_number: string | null;
        asset_code?: string | null;
        asset_name?: string | null;
        equipment_label?: string;
        operational_status?: string | null;
        attribution_count: number;
        attributed_litres: number | null;
        open_attention: number;
        open_recon_gaps: number;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(res);
  },

  async listFuelSources(params?: { source_type?: string }) {
    const res = await api.get('/pmv/fuel/sources', { params });
    const data = unwrap<{ items?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>>(
      res
    );
    if (Array.isArray(data)) return { items: data };
    return { items: data?.items ?? [] };
  },

  async createFuelSource(body: {
    name: string;
    source_type: string;
    code?: string;
    capacity_litres?: number | null;
    warehouse_id?: string | null;
    pmv_profile_id?: string | null;
  }) {
    const res = await api.post('/pmv/fuel/sources', body);
    return unwrap(res);
  },

  async listFuelReconciliation(params?: { status?: string; limit?: number }) {
    const res = await api.get('/pmv/fuel/reconciliation', { params });
    return unwrap<{ items: Array<Record<string, any>>; total: number }>(res);
  },

  async resolveFuelReconciliation(
    id: string,
    body: { status?: string; resolution_notes?: string; resolution_reference?: string }
  ) {
    const res = await api.post(`/pmv/fuel/reconciliation/${id}/resolve`, body);
    return unwrap(res);
  },

  async dismissFuelReconciliation(id: string, body: { resolution_notes?: string }) {
    const res = await api.post(`/pmv/fuel/reconciliation/${id}/dismiss`, body);
    return unwrap(res);
  },

  async listFuelAttention(params?: { status?: string; limit?: number }) {
    const res = await api.get('/pmv/fuel/attention', { params });
    return unwrap<{ items: Array<Record<string, any>>; total: number }>(res);
  },

  async ackFuelAttention(id: string) {
    const res = await api.post(`/pmv/fuel/attention/${id}/ack`);
    return unwrap(res);
  },

  async dismissFuelAttention(id: string, body: { dismiss_reason: string }) {
    const res = await api.post(`/pmv/fuel/attention/${id}/dismiss`, body);
    return unwrap(res);
  },

  async getFuelProjectIntelligence(projectId: string) {
    const res = await api.get(`/pmv/fuel/project/${projectId}`);
    return unwrap(res);
  },

  async getFuelDiagnostics() {
    const res = await api.get('/pmv/fuel/diagnostics');
    return unwrap(res);
  },

  async getFuelPolicies() {
    const res = await api.get('/pmv/fuel/policies');
    return unwrap(res);
  },

  // ── PMV 2.0 Wave 5 — Reliability Intelligence ──────────────────────────
  async getReliabilityMeta() {
    const res = await api.get('/pmv/reliability/meta');
    return unwrap(res);
  },

  async getReliabilityControlTower() {
    const res = await api.get('/pmv/reliability/control-tower');
    return unwrap<{
      open_attention?: number;
      attention_requiring_action?: number;
      critical_health: number;
      unknown_health: number;
      active_predictions: number;
      open_breakdowns?: number;
      failures_90d?: number;
      health_by_state?: Record<string, number>;
      fleet_health?: Record<string, number>;
      note?: string;
      wave?: string;
      telemetry_track?: string;
    }>(res);
  },

  async getReliabilityRegister(params?: {
    q?: string;
    health_state?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/reliability/equipment', { params });
    return unwrap<{
      items: Array<{
        pmv_profile_id: string;
        fleet_number?: string | null;
        asset_code?: string | null;
        asset_name?: string | null;
        equipment_label?: string;
        health_state: string;
        health_score?: number | null;
        health_confidence: string;
        primary_driver?: string | null;
        mtbf_hours?: number | null;
        risk_band?: string | null;
        open_breakdown?: boolean;
        open_reliability_attention?: number;
        criticality?: string | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(res);
  },

  async getEquipmentReliabilityHealth(profileId: string) {
    const res = await api.get(`/pmv/reliability/equipment/${profileId}/health`);
    return unwrap<{
      pmv_profile_id?: string;
      health_state: string;
      health_score?: number | null;
      health_confidence: string;
      primary_driver?: string | null;
      mtbf_hours?: number | null;
      mttr_hours?: number | null;
      availability_pct?: number | null;
      equipment_label?: string;
      note?: string;
      explanation_json?: Record<string, unknown> | null;
    }>(res);
  },

  async listReliabilityAttention(params?: { status?: string; limit?: number }) {
    const res = await api.get('/pmv/reliability/attention', { params });
    return unwrap<{ items: Array<Record<string, any>>; total: number }>(res);
  },

  async ackReliabilityAttention(id: string) {
    const res = await api.post(`/pmv/reliability/attention/${id}/ack`);
    return unwrap(res);
  },

  async dismissReliabilityAttention(id: string, body: { dismiss_reason: string }) {
    const res = await api.post(`/pmv/reliability/attention/${id}/dismiss`, body);
    return unwrap(res);
  },

  async listReliabilityFailures(params?: {
    pmv_profile_id?: string;
    failure_system?: string;
    limit?: number;
  }) {
    const res = await api.get('/pmv/reliability/failures', { params });
    return unwrap<{ items: Array<Record<string, any>>; total: number }>(res);
  },

  async listReliabilityFaults(params?: { status?: string; limit?: number }) {
    const res = await api.get('/pmv/reliability/faults', { params });
    return unwrap<{ items: Array<Record<string, any>>; total: number }>(res);
  },

  async listReliabilityPredictions(params?: { status?: string; limit?: number }) {
    const res = await api.get('/pmv/reliability/predictions', { params });
    return unwrap<{ items: Array<Record<string, any>>; total: number }>(res);
  },

  async dismissReliabilityPrediction(id: string) {
    const res = await api.post(`/pmv/reliability/predictions/${id}/dismiss`);
    return unwrap(res);
  },

  async getReliabilityForecast() {
    const res = await api.get('/pmv/reliability/forecast');
    return unwrap<{
      planned?: { label?: string; items?: Array<Record<string, any>> } | Array<Record<string, any>>;
      predicted?: { label?: string; items?: Array<Record<string, any>> } | Array<Record<string, any>>;
      note?: string;
    }>(res);
  },

  async listReliabilityConditions(params?: { pmv_profile_id?: string; limit?: number }) {
    const res = await api.get('/pmv/reliability/conditions', { params });
    return unwrap<{ items: Array<Record<string, any>>; total: number }>(res);
  },

  async recalculateReliabilityHealth(profileId: string) {
    const res = await api.post(`/pmv/reliability/equipment/${profileId}/recalculate-health`);
    return unwrap(res);
  },

  async openReliabilityMaintenance(
    profileId: string,
    body?: { title?: string; description?: string; priority?: string; attention_id?: string }
  ) {
    const res = await api.post(`/pmv/reliability/equipment/${profileId}/open-maintenance`, body ?? {});
    return unwrap(res);
  },

  async getReliabilityPareto() {
    const res = await api.get('/pmv/reliability/pareto');
    return unwrap(res);
  },

  async getReliabilityBadActors() {
    const res = await api.get('/pmv/reliability/bad-actors');
    return unwrap(res);
  },

  async getControlTower(params?: { operating_date?: string; publish_signals?: boolean }) {
    const res = await api.get('/pmv/control-tower', {
      params: {
        operating_date: params?.operating_date,
        publish_signals: params?.publish_signals === false ? '0' : undefined,
      },
    });
    return unwrap<PmvControlTowerSnapshot>(res);
  },

  async listInternalHireRates() {
    const res = await api.get('/pmv/internal-hire-rates');
    return unwrap<PmvInternalHireRate[]>(res);
  },

  async createInternalHireRate(body: Record<string, unknown>) {
    const res = await api.post('/pmv/internal-hire-rates', body);
    return unwrap<PmvInternalHireRate>(res);
  },

  async patchInternalHireRate(id: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/internal-hire-rates/${id}`, body);
    return unwrap<PmvInternalHireRate>(res);
  },

  // —— Connected Fleet (PMV 2.0 Wave 0) ——
  async getConnectedSummary() {
    const res = await api.get('/pmv/connected/summary');
    return unwrap<PmvConnectedSummary>(res);
  },

  async listConnectedProviders() {
    const res = await api.get('/pmv/connected/providers');
    return unwrap<PmvConnectedProvider[]>(res);
  },

  async createConnectedProvider(body: Record<string, unknown>) {
    const res = await api.post('/pmv/connected/providers', body);
    return unwrap<PmvConnectedProvider>(res);
  },

  async patchConnectedProvider(id: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/connected/providers/${id}`, body);
    return unwrap<PmvConnectedProvider>(res);
  },

  async testConnectedProvider(id: string) {
    const res = await api.post(`/pmv/connected/providers/${id}/test-connection`, {});
    return unwrap<{
      ok: boolean;
      auth_ok: boolean;
      account_label?: string;
      devices_visible?: number;
      capabilities?: string[];
      message: string;
    }>(res);
  },

  async syncConnectedProviderNow(id: string) {
    const res = await api.post(`/pmv/connected/providers/${id}/sync-now`, {});
    return unwrap<{ id: string; status: string; events_accepted: number; duplicates: number }>(res);
  },

  async pauseConnectedProvider(id: string) {
    const res = await api.post(`/pmv/connected/providers/${id}/pause`, {});
    return unwrap(res);
  },

  async resumeConnectedProvider(id: string) {
    const res = await api.post(`/pmv/connected/providers/${id}/resume`, {});
    return unwrap(res);
  },

  async listConnectedProviderSyncRuns(id: string, limit = 20) {
    const res = await api.get(`/pmv/connected/providers/${id}/sync-runs`, { params: { limit } });
    return unwrap<
      Array<{
        id: string;
        mode: string;
        status: string;
        events_fetched?: number;
        events_accepted?: number;
        duplicates?: number;
        error_message?: string | null;
        started_at?: string;
        completed_at?: string | null;
      }>
    >(res);
  },

  async listReconciliationCases(params?: { status?: string; case_type?: string }) {
    const res = await api.get('/pmv/connected/reconciliation', { params });
    return unwrap<
      Array<{
        id: string;
        pmv_profile_id: string;
        case_type: string;
        severity: string;
        status: string;
        detail: string | null;
        recommended_action: string | null;
        evidence_json?: Record<string, unknown> | null;
        dismissal_reason?: string | null;
        created_at: string;
      }>
    >(res);
  },

  async getReconciliationCase(id: string) {
    const res = await api.get(`/pmv/connected/reconciliation/${id}`);
    return unwrap<{
      id: string;
      pmv_profile_id: string;
      case_type: string;
      severity: string;
      status: string;
      detail: string | null;
      recommended_action: string | null;
      evidence_json?: Record<string, unknown> | null;
      dismissal_reason?: string | null;
      resolution_reference?: string | null;
      created_at: string;
      reviewed_at?: string | null;
    }>(res);
  },

  async refreshReconciliationCases() {
    const res = await api.post('/pmv/connected/reconciliation/refresh', {});
    return unwrap<{ scanned: number; created: number }>(res);
  },

  async dismissReconciliationCase(id: string, reason: string) {
    const res = await api.post(`/pmv/connected/reconciliation/${id}/dismiss`, { reason });
    return unwrap(res);
  },

  async promoteReconciliationCase(id: string) {
    const res = await api.post(`/pmv/connected/reconciliation/${id}/promote`, {});
    return unwrap<{
      case: { id: string; status: string };
      authoritative_action: { kind: string; path: string; note: string };
    }>(res);
  },

  async listConnectedDevices(params?: {
    provider_id?: string;
    unassigned_only?: boolean;
    page?: number;
    pageSize?: number;
    search?: string;
  }) {
    const res = await api.get('/pmv/connected/devices', {
      params: {
        provider_id: params?.provider_id,
        unassigned_only: params?.unassigned_only ? 'true' : undefined,
        page: params?.page,
        pageSize: params?.pageSize,
        search: params?.search,
      },
    });
    return unwrap<{
      items: PmvConnectedDevice[];
      total: number;
      page: number;
      pageSize: number;
    }>(res);
  },

  async createConnectedDevice(body: Record<string, unknown>) {
    const res = await api.post('/pmv/connected/devices', body);
    return unwrap<PmvConnectedDevice>(res);
  },

  async bulkOnboardConnectedDevices(
    providerId: string,
    body: {
      mode?: 'atomic' | 'partial';
      effective_from?: string;
      rows: Array<{
        external_device_id: string;
        serial_number?: string | null;
        device_type?: string | null;
        pmv_profile_id?: string | null;
        register_only?: boolean;
      }>;
    }
  ) {
    const res = await api.post(`/pmv/connected/providers/${providerId}/bulk-onboard`, body);
    return unwrap<{ total: number; ok: number; error: number; results: unknown[] }>(res);
  },

  async discoverConnectedDevices(providerId: string) {
    const res = await api.post(`/pmv/connected/providers/${providerId}/discover-devices`, {});
    return unwrap<{
      devices: Array<{
        external_device_id: string;
        display_name?: string;
        serial_number?: string | null;
        device_type?: string | null;
        already_registered?: boolean;
        match_confidence?: string;
        suggested_match?: string | null;
        suggested_label?: string | null;
      }>;
      counts?: Record<string, number>;
    }>(res);
  },

  async listConnectedDeviceAssignments(deviceId: string) {
    const res = await api.get(`/pmv/connected/devices/${deviceId}/assignments`);
    return unwrap<
      Array<{
        id: string;
        pmv_profile_id: string;
        asset_id: string;
        effective_from: string;
        effective_to?: string | null;
        assignment_reason?: string | null;
      }>
    >(res);
  },

  async assignConnectedDevice(deviceId: string, body: { pmv_profile_id: string; effective_from?: string }) {
    const res = await api.post(`/pmv/connected/devices/${deviceId}/assign`, body);
    return unwrap<unknown>(res);
  },

  async unassignConnectedDevice(deviceId: string, body?: { effective_to?: string }) {
    const res = await api.post(`/pmv/connected/devices/${deviceId}/unassign`, body ?? {});
    return unwrap<unknown>(res);
  },

  async listEquipmentConnectivity() {
    const res = await api.get('/pmv/connected/equipment-connectivity');
    return unwrap<Array<{ pmv_profile_id: string; connectivity: string | null; last_seen_at?: string | null }>>(res);
  },

  async getEquipmentConnectedState(pmvId: string) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/state`);
    return unwrap<PmvEquipmentConnectedState>(res);
  },

  async listEquipmentConnectedSignals(pmvId: string, params?: { signal_code?: string; limit?: number }) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/signals`, { params });
    return unwrap<PmvTelemetrySignalRow[]>(res);
  },

  async listEquipmentLocationHistory(
    pmvId: string,
    params?: { limit?: number; from?: string; to?: string; max_scan?: number }
  ) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/location-history`, {
      params: params ?? { limit: 200 },
    });
    return unwrap<PmvLocationHistoryResponse>(res);
  },

  async getEquipmentMovement(pmvId: string) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/movement`);
    return unwrap<PmvMovementView>(res);
  },

  async compareEquipmentSpatial(pmvId: string) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/spatial-compare`);
    return unwrap<{
      expected: {
        source: string;
        allocation_id?: string | null;
        project_id?: string | null;
        operational_status?: string | null;
      };
      observed: {
        quality: string;
        inside_project_ids: string[];
        restricted: boolean;
        movement_state: string | null;
        primary_geofence_code: string | null;
      };
      findings: Array<{ type: string; detail: string }>;
    }>(res);
  },

  async listEquipmentSpatialEvents(
    pmvId: string,
    params?: { event_type?: string; limit?: number }
  ) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/spatial-events`, { params });
    return unwrap<PmvSpatialEventRow[]>(res);
  },

  async listEquipmentSpatialAttention(pmvId: string, params?: { status?: string }) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/spatial-attention`, { params });
    return unwrap<
      Array<{
        id: string;
        case_type: string;
        severity: string;
        status: string;
        detail: string | null;
        recommended_action: string | null;
        created_at: string;
      }>
    >(res);
  },

  async listEquipmentGeofenceMembership(pmvId: string) {
    const res = await api.get(`/pmv/connected/equipment/${pmvId}/geofence-membership`);
    return unwrap<
      Array<{
        geofence_id: string;
        stable_membership: string;
        last_observed_at: string | null;
        last_quality: string | null;
      }>
    >(res);
  },

  async previewBulkOnboardCsv(providerId: string, csv: string) {
    const res = await api.post(`/pmv/connected/providers/${providerId}/bulk-onboard/preview-csv`, { csv });
    return unwrap<{
      columns: string[];
      rows: Array<{ status: string; external_device_id?: string; message?: string }>;
      valid: number;
      invalid: number;
      note?: string;
    }>(res);
  },

  async getConnectedDiagnostics() {
    const res = await api.get('/pmv/connected/diagnostics');
    return unwrap<PmvConnectedDiagnostics>(res);
  },

  async retryConnectedQuarantine(id: string) {
    const res = await api.post(`/pmv/connected/quarantine/${id}/retry`, {});
    return unwrap<{ ok: boolean }>(res);
  },

  async simulateConnectedIngest(body: Record<string, unknown>) {
    const res = await api.post('/pmv/connected/simulate-ingest', body);
    return unwrap<{ accepted: number; duplicates: number; correlation_id: string }>(res);
  },

  // —— Live Fleet Map (Wave 2.6/2.7) ——
  async getFleetMap(params?: {
    project_id?: string;
    connectivity?: string;
    movement_state?: string;
    operational_status?: string;
    attention_only?: boolean;
    include_stale?: boolean;
    q?: string;
    min_lat?: number;
    max_lat?: number;
    min_lon?: number;
    max_lon?: number;
    cluster?: boolean;
    limit?: number;
  }) {
    const res = await api.get('/pmv/connected/map/fleet', { params });
    return unwrap<PmvFleetMapResponse>(res);
  },

  async getMapGeofences(params?: { project_id?: string; type?: string }) {
    const res = await api.get('/pmv/connected/map/geofences', { params });
    return unwrap<PmvMapGeofence[]>(res);
  },

  async getFleetMapCard(pmvId: string) {
    const res = await api.get(`/pmv/connected/map/equipment/${pmvId}/card`);
    return unwrap<PmvFleetMapCard>(res);
  },

  async getProjectSpatialMap(projectId: string) {
    const res = await api.get(`/pmv/connected/map/project/${projectId}`);
    return unwrap<{
      project_id: string;
      generated_at: string;
      summary: {
        assigned: number;
        observed_on_site: number;
        observed_outside: number;
        no_recent_location: number;
        in_transit: number;
        moving: number;
        attention: number;
        unexpected_observed: number;
        note: string;
      };
      equipment: Array<{
        pmv_profile_id: string;
        allocation_id: string;
        allocation_status: string;
        category: string;
        fleet_number: string | null;
        asset_code: string | null;
        asset_name: string | null;
        operational_status: string | null;
        movement_state: string;
        location: PmvFleetMapMarker['location'];
        attention: PmvFleetMapMarker['attention'];
        map_eligible: boolean;
      }>;
      unexpected_observed: Array<{
        pmv_profile_id: string;
        fleet_number: string | null;
        asset_code: string | null;
        asset_name: string | null;
        expected_project_id: string | null;
        location: PmvFleetMapMarker['location'];
        note: string;
      }>;
      markers: PmvFleetMapMarker[];
      geofences: PmvMapGeofence[];
      authority: {
        mutates_allocation: boolean;
        mutates_ops_status: boolean;
        observed_only: boolean;
        assigned_neq_observed: boolean;
      };
    }>(res);
  },

  async listSpatialAttention(params?: {
    pmv_profile_id?: string;
    status?: string;
    limit?: number;
  }) {
    const res = await api.get('/pmv/connected/spatial/attention', { params });
    return unwrap<
      Array<{
        id: string;
        pmv_profile_id: string;
        case_type: string;
        severity: string;
        status: string;
        detail: string | null;
        recommended_action: string | null;
        created_at: string;
        updated_at?: string;
      }>
    >(res);
  },

  async promoteSpatialAttention(id: string) {
    const res = await api.post(`/pmv/connected/spatial/attention/${id}/promote`, {});
    return unwrap<{ case: unknown; authoritative_action: { path: string; note: string } }>(res);
  },

  // —— Geofences (Wave 2.1 / 2.8 admin UX) ——
  async listGeofences(params?: {
    status?: string;
    type?: string;
    project_id?: string;
    include_inactive?: boolean;
  }) {
    const res = await api.get('/pmv/connected/geofences', { params });
    return unwrap<PmvGeofence[]>(res);
  },

  async getGeofence(id: string) {
    const res = await api.get(`/pmv/connected/geofences/${id}`);
    return unwrap<PmvGeofence>(res);
  },

  async createGeofence(body: PmvGeofenceCreatePayload) {
    const res = await api.post('/pmv/connected/geofences', body);
    return unwrap<PmvGeofence>(res);
  },

  async patchGeofence(id: string, body: Partial<PmvGeofenceCreatePayload> & { change_reason?: string }) {
    const res = await api.patch(`/pmv/connected/geofences/${id}`, body);
    return unwrap<PmvGeofence>(res);
  },

  async validateGeofenceGeometry(body: {
    geometry_type: string;
    center_lat?: number;
    center_lon?: number;
    radius_m?: number;
    geometry_json?: unknown;
  }) {
    const res = await api.post('/pmv/connected/geofences/validate-geometry', body);
    return unwrap<{ ok: boolean; message: string }>(res);
  },

  async activateGeofence(id: string) {
    const res = await api.post(`/pmv/connected/geofences/${id}/activate`, {});
    return unwrap<PmvGeofence>(res);
  },

  async deactivateGeofence(id: string) {
    const res = await api.post(`/pmv/connected/geofences/${id}/deactivate`, {});
    return unwrap<PmvGeofence>(res);
  },

  async getGeofenceOccupancy(id: string) {
    const res = await api.get(`/pmv/connected/geofences/${id}/occupancy`);
    return unwrap<{
      geofence_id: string;
      type: string;
      counts: { CURRENT: number; STALE: number; UNKNOWN: number };
      equipment: Array<{
        pmv_profile_id: string;
        presence: string;
        fleet_number: string | null;
        asset_code: string | null;
        asset_name: string | null;
        freshness: string | null;
        expected_project_id: string | null;
        last_observed_at: string | null;
      }>;
      note: string;
    }>(res);
  },

  async listGeofenceRevisions(id: string) {
    const res = await api.get(`/pmv/connected/geofences/${id}/revisions`);
    return unwrap<PmvGeofenceRevision[]>(res);
  },

  // —— Component lifecycle (Wave 3) ——
  async getTyreSchematic(pmvProfileId: string) {
    const res = await api.get(`/pmv/components/equipment/${pmvProfileId}/tyre-schematic`);
    return unwrap<PmvTyreSchematicResponse>(res);
  },

  async getBatterySchematic(pmvProfileId: string) {
    const res = await api.get(`/pmv/components/equipment/${pmvProfileId}/battery-schematic`);
    return unwrap<{
      pmv_profile_id: string;
      cards: Array<{
        position_id: string;
        position_code: string;
        display_label: string;
        status: 'OCCUPIED' | 'VACANT';
        component_id: string | null;
        component_number: string | null;
        condition_code: string | null;
        battery_type: string | null;
        nominal_voltage: number | null;
        latest_voltage: number | null;
        latest_soh_pct: number | null;
        latest_health_source: string | null;
        attention: string[];
      }>;
      empty_reason: string | null;
      note?: string;
    }>(res);
  },

  async getComponentMeta() {
    const res = await api.get('/pmv/components/meta');
    return unwrap<{
      presets?: {
        tyre_4x2?: PmvPositionTemplateSlotInput[];
        battery_dual?: PmvPositionTemplateSlotInput[];
      };
      template_family_scopes?: string[];
    }>(res);
  },

  async listPositionTemplates(params?: { family_scope?: string; include_inactive?: boolean }) {
    const res = await api.get('/pmv/components/position-templates', {
      params: {
        family_scope: params?.family_scope,
        include_inactive: params?.include_inactive ? '1' : undefined,
      },
    });
    return unwrap<PmvPositionTemplateSummary[]>(res);
  },

  async createPositionTemplate(body: {
    code: string;
    name: string;
    family_scope?: string;
    description?: string | null;
    slots?: PmvPositionTemplateSlotInput[];
  }) {
    const res = await api.post('/pmv/components/position-templates', body);
    return unwrap<PmvPositionTemplateSummary>(res);
  },

  async applyEquipmentLayout(
    pmvProfileId: string,
    body: { template_id?: string; from_classification?: boolean; force?: boolean; notes?: string | null }
  ) {
    const res = await api.post(`/pmv/components/equipment/${pmvProfileId}/layout/apply`, body);
    return unwrap<Record<string, unknown>>(res);
  },

  async getTyreDetails(componentId: string) {
    const res = await api.get(`/pmv/components/${componentId}/tyre`);
    return unwrap<{ component: Record<string, unknown>; tyre: Record<string, unknown> }>(res);
  },

  async patchTyreDetails(componentId: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/components/${componentId}/tyre`, body);
    return unwrap<{ component: Record<string, unknown>; tyre: Record<string, unknown> }>(res);
  },

  async listTyreInspections(componentId: string, params?: { limit?: number }) {
    const res = await api.get(`/pmv/components/${componentId}/tyre/inspections`, { params });
    return unwrap<unknown[]>(res);
  },

  async recordTyreInspection(componentId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/components/${componentId}/tyre/inspections`, body);
    return unwrap<{ inspection: unknown; idempotent_replay?: boolean }>(res);
  },

  async rotateComponents(body: Record<string, unknown>) {
    const res = await api.post('/pmv/components/rotate', body);
    return unwrap<Record<string, unknown>>(res);
  },

  async installComponent(body: {
    component_id: string;
    pmv_profile_id: string;
    equipment_position_id?: string;
    position_code?: string;
    notes?: string | null;
    idempotency_key?: string | null;
  }) {
    const res = await api.post('/pmv/components/install', body);
    return unwrap<{ installation: Record<string, unknown>; idempotent_replay?: boolean }>(res);
  },

  async removeComponent(
    componentId: string,
    body: {
      removal_reason: string;
      removal_destination: string;
      notes?: string | null;
      idempotency_key?: string | null;
    }
  ) {
    const res = await api.post(`/pmv/components/${componentId}/remove`, body);
    return unwrap<{ installation: Record<string, unknown>; idempotent_replay?: boolean }>(res);
  },

  async replaceComponent(body: Record<string, unknown>) {
    const res = await api.post('/pmv/components/replace', body);
    return unwrap<Record<string, unknown>>(res);
  },

  async recordComponentRepair(componentId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/components/${componentId}/repairs`, body);
    return unwrap<Record<string, unknown>>(res);
  },

  async dispatchTyreRetread(componentId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/components/${componentId}/tyre/retreads/dispatch`, body);
    return unwrap<Record<string, unknown>>(res);
  },

  async returnTyreRetread(componentId: string, retreadId: string, body: Record<string, unknown>) {
    const res = await api.post(
      `/pmv/components/${componentId}/tyre/retreads/${retreadId}/return`,
      body
    );
    return unwrap<Record<string, unknown>>(res);
  },

  async listComponents(params?: {
    page?: number;
    pageSize?: number;
    q?: string;
    family?: string;
    status?: string;
    condition?: string;
    manufacturer?: string;
    model?: string;
    installed_only?: boolean;
    available_only?: boolean;
    pmv_profile_id?: string;
    location_type?: string;
  }) {
    const res = await api.get('/pmv/components', {
      params: {
        ...params,
        installed_only: params?.installed_only ? '1' : undefined,
        available_only: params?.available_only ? '1' : undefined,
      },
    });
    return unwrap<{
      items: PmvComponentRegisterItem[];
      total: number;
      page: number;
      pageSize: number;
    }>(res);
  },

  async listComponentDefinitions(params?: { family?: string; q?: string; limit?: number }) {
    const res = await api.get('/pmv/components/definitions', { params });
    return unwrap<PmvComponentDefinition[]>(res);
  },

  async createComponentDefinition(body: {
    family: string;
    code: string;
    name: string;
    brand?: string | null;
    model?: string | null;
    manufacturer?: string | null;
    description?: string | null;
    specification_json?: Record<string, unknown> | null;
    expected_life_basis?: string | null;
    expected_life_value?: number | null;
  }) {
    const res = await api.post('/pmv/components/definitions', body);
    return unwrap<PmvComponentDefinition>(res);
  },

  async createComponent(body: {
    component_definition_id: string;
    serial_number?: string | null;
    manufacturer_serial?: string | null;
    status?: string;
    condition_code?: string | null;
    current_location_type?: string;
    current_location_reference?: string | null;
    ownership?: string | null;
    provenance?: string;
    data_confidence?: string;
    notes?: string | null;
    metadata_json?: Record<string, unknown> | null;
  }) {
    const res = await api.post('/pmv/components', body);
    return unwrap<Record<string, unknown>>(res);
  },

  async patchComponent(
    componentId: string,
    body: {
      serial_number?: string | null;
      manufacturer_serial?: string | null;
      status?: string;
      condition_code?: string | null;
      current_location_type?: string;
      current_location_reference?: string | null;
      ownership?: string | null;
      is_active?: boolean;
      metadata_json?: Record<string, unknown> | null;
    }
  ) {
    const res = await api.patch(`/pmv/components/${componentId}`, body);
    return unwrap<Record<string, unknown>>(res);
  },

  async getComponentPassport(componentId: string) {
    const res = await api.get(`/pmv/components/${componentId}/passport`);
    return unwrap<PmvComponentPassport>(res);
  },

  async getComponentControlTower() {
    const res = await api.get('/pmv/components/control-tower');
    return unwrap<PmvComponentControlTowerSnapshot>(res);
  },

  async getComponentAttentionStrip() {
    const res = await api.get('/pmv/components/attention/strip');
    return unwrap<{
      headlines: string[];
      tyres_requiring_attention: number;
      batteries_requiring_attention: number;
      warranty_expiring_30d: number;
      drill_href: string;
      note: string;
    }>(res);
  },

  async getComponentImportTemplate() {
    const res = await api.get('/pmv/components/import/template');
    return unwrap<{ headers: string[]; sample_csv: string; note: string }>(res);
  },

  async previewComponentImport(body: { csv?: string; rows?: Record<string, unknown>[] }) {
    const res = await api.post('/pmv/components/import/preview', body);
    return unwrap<{
      rows: Array<{
        row_number: number;
        ok: boolean;
        errors: Array<{ code: string; message: string }>;
        warnings: string[];
        normalized: Record<string, unknown>;
      }>;
      ok_count: number;
      error_count: number;
      can_commit: boolean;
    }>(res);
  },

  async commitComponentImport(body: { csv?: string; rows?: Record<string, unknown>[]; dry_run?: boolean }) {
    const res = await api.post('/pmv/components/import/commit', body);
    return unwrap<{ created: number; results: unknown[]; note?: string }>(res);
  },

  async getComponentDiagnostics() {
    const res = await api.get('/pmv/components/diagnostics');
    return unwrap<{
      component_count: number;
      issue_count: number;
      issues: Array<{ code: string; component_id?: string; message: string }>;
      auto_fix: false;
      note: string;
    }>(res);
  },

  async listComponentAttention(params?: { family?: string; limit?: number }) {
    const res = await api.get('/pmv/components/attention', { params });
    return unwrap<{
      items: Array<{
        component_id: string;
        component_number: string;
        family: string;
        pmv_profile_id: string | null;
        fleet_number: string | null;
        labels: string[];
        max_severity: string | null;
      }>;
      scanned: number;
    }>(res);
  },

  async previewComponentCompatibility(body: Record<string, unknown>) {
    const res = await api.post('/pmv/components/compatibility/preview', body);
    return unwrap<{
      severity: string;
      can_install: boolean;
      findings: Array<{ code: string; severity: string; message: string }>;
      note?: string;
    }>(res);
  },
};

export interface PmvComponentDefinition {
  id: string;
  family: string;
  code: string;
  name: string;
  brand?: string | null;
  model?: string | null;
  manufacturer?: string | null;
  description?: string | null;
  is_active?: boolean;
  specification_json?: Record<string, unknown> | null;
}

export interface PmvComponentRegisterItem {
  id: string;
  component_number: string;
  family: string;
  status: string;
  condition_code: string | null;
  specification: string | null;
  definition_code: string | null;
  definition_name: string | null;
  manufacturer: string | null;
  brand: string | null;
  model: string | null;
  current_equipment: string | null;
  pmv_profile_id: string | null;
  position_code: string | null;
  location_type: string;
  location_reference: string | null;
  warranty: string;
  warranty_end: string | null;
  tyre: {
    size: string | null;
    pattern: string | null;
    tread_mm: number | null;
    pressure_psi: number | null;
    pressure_exception: string | null;
    retread_count: number;
  } | null;
  battery: {
    type: string | null;
    voltage: number | null;
    nominal_voltage: number | null;
    soh_pct: number | null;
  } | null;
  attention: string[];
  attention_pending_engine?: boolean;
}

export type PmvComponentPassport = {
  hero: {
    component_number: string;
    family: string;
    definition_name: string | null;
    brand: string | null;
    model: string | null;
    size_or_spec: string | null;
    status: string;
    condition_code: string | null;
    equipment_label: string | null;
    position_code: string | null;
    installed_at: string | null;
    usage: { status: string; usage: number | null; reason: string | null };
    tread_mm: string | number | null;
    warranty: string;
    warranty_end: string | null;
  };
  primary_action: { label: string; href: string; reason: string } | null;
  tabs: string[];
  component: Record<string, unknown> & {
    family: string;
    serial_number?: string | null;
    purchase_reference?: string | null;
    provenance?: string;
    current_location_type?: string;
    current_location_reference?: string | null;
    definition?: { code?: string; name?: string } | null;
  };
  equipment: {
    pmv_profile_id: string;
    fleet_number: string | null;
    operational_status: string;
  } | null;
  genealogy: {
    events: Record<string, unknown>[];
    installations: Record<string, unknown>[];
  };
  tyre: { tyre: Record<string, unknown> } | null;
  tyre_inspections: unknown[];
  battery: { battery: Record<string, unknown> } | null;
  battery_inspections: unknown[];
  repairs: unknown[];
  retreads: unknown[];
  warranties: unknown[];
  integration: {
    links: Record<string, unknown>[];
    boundaries?: { note?: string };
  };
  cost: {
    deferred: boolean;
    note: string;
    performance?: Record<string, unknown> | null;
  };
  attention: { deferred: boolean; hints: string[]; note: string; findings?: unknown[] };
};

export type PmvComponentControlTowerSnapshot = {
  as_of: string;
  health: {
    total_components: number;
    tyres_installed: number;
    tyres_available: number;
    tyres_requiring_attention: number;
    batteries_installed: number;
    batteries_available: number;
    batteries_requiring_attention: number;
  };
  lifecycle: {
    under_repair: number;
    under_retread: number;
    retread_candidates: number;
    warranty_expiring_30d: number;
    warranty_expired: number;
  };
  dominant_attention: Array<{ code: string; count: number }>;
  headlines: string[];
  attention_items: Array<{
    component_id: string;
    component_number: string;
    family: string;
    status: string;
    fleet_number: string | null;
    position_code: string | null;
    labels: string[];
    max_severity: string | null;
  }>;
  economics: {
    deferred: boolean;
    note: string;
    summary?: {
      unit_cost_benchmarks?: {
        tyre?: { n: number; median_per_km: number | null; insufficient: boolean };
        battery?: { n: number; median_per_month: number | null; insufficient: boolean };
      };
      warranty?: { expiring_30d: number; open_claims: number };
      removal_analytics?: Array<{ family: string; reason: string; count: number }>;
      supplier_model_performance?: Array<{
        code: string;
        sample_n: number;
        ranking: string;
        note: string | null;
      }>;
    };
  };
};

export interface PmvTyreSchematicCard {
  position_id: string;
  position_code: string;
  display_label: string;
  axle_group: string | null;
  side: string | null;
  dual_role: string | null;
  is_spare: boolean;
  status: 'OCCUPIED' | 'VACANT';
  component_id: string | null;
  component_number: string | null;
  condition_code: string | null;
  size_code: string | null;
  casing_type: string | null;
  retread_count: number | null;
  tread_depth_mm: number | null;
  pressure_target_psi: number | null;
  pressure_exception: string | null;
  attention: string[];
}

export interface PmvTyreSchematicResponse {
  pmv_profile_id: string;
  layout: unknown;
  schematic_groups: Array<{
    group_key: string;
    label: string;
    positions: Array<{ position_code: string; display_label?: string }>;
  }>;
  cards: PmvTyreSchematicCard[];
  empty_reason: string | null;
  note?: string;
}

export interface PmvPositionTemplateSlotInput {
  position_code: string;
  display_label: string;
  position_kind: string;
  family: string;
  axle_group?: string | null;
  side?: string | null;
  dual_role?: string | null;
  sort_order?: number;
  is_exclusive?: boolean;
  is_required?: boolean;
  is_spare?: boolean;
}

export interface PmvPositionTemplateSummary {
  id: string;
  code: string;
  name: string;
  family_scope: string;
  is_active: boolean;
  description?: string | null;
}

export interface PmvRequest {
  id: string;
  request_number: string;
  status: string;
  project_id: string;
  priority: string;
  submitted_at?: string | null;
}

export interface PmvRequestRequirement {
  id: string;
  classification_id: string;
  quantity_required: number;
  quantity_fulfilled: number;
  required_from: string;
  required_until: string;
  criteria_json?: unknown[] | null;
}

export interface PmvRequestDetail extends PmvRequest {
  requirements: PmvRequestRequirement[];
  justification?: string | null;
}

export interface PmvMatchCriteriaResult {
  attribute_code?: string;
  label?: string;
  required?: unknown;
  actual?: unknown;
  pass?: boolean;
  operator?: string;
  message?: string;
}

export interface PmvMatchResult {
  profile_id: string;
  asset_code: string;
  asset_name: string;
  match_status: string;
  operational_status: string;
  /** Present when matching engine returns per-criterion PASS/FAIL detail */
  criteria_results?: PmvMatchCriteriaResult[];
}

export interface PmvAvailabilityItem {
  profile_id: string;
  asset_code: string;
  asset_name: string;
  availability: string;
  match_status: string;
  conflict_reason?: string;
}

export interface PmvReservation {
  id: string;
  pmv_profile_id: string;
  request_id?: string | null;
  requirement_id?: string | null;
  project_id: string;
  from_date: string;
  until_date: string;
  status: string;
}

export interface PmvAllocation {
  id: string;
  allocation_number: string;
  pmv_profile_id: string;
  project_id: string;
  planned_start: string;
  planned_end: string;
  allocation_status: string;
}

export interface PmvDailyLog {
  id: string;
  log_number: string;
  pmv_profile_id: string;
  allocation_id: string;
  project_id: string;
  operating_date: string;
  shift_code: string;
  status: string;
  working_minutes: number;
  idle_minutes: number;
  standby_minutes: number;
  breakdown_minutes: number;
  available_minutes: number;
  scheduled_minutes: number;
  sheet_id?: string | null;
}

export interface PmvTimeEntry {
  id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  activity_type: string;
  reason_code?: string | null;
  description?: string | null;
}

export interface PmvOutputEntry {
  id: string;
  activity_description: string;
  quantity: number;
  uom: string;
}

export interface PmvDailyLogDetail extends PmvDailyLog {
  timeEntries?: PmvTimeEntry[];
  outputs?: PmvOutputEntry[];
  openingMeter?: { reading_value: number } | null;
  closingMeter?: { reading_value: number } | null;
  work_description?: string | null;
  remarks?: string | null;
  submitted_at?: string | null;
  certified_at?: string | null;
  certified_by?: string | null;
  return_reason?: string | null;
}

export interface PmvDailyLogAnalytics {
  utilization: {
    workingUtilization: number | null;
    scheduledUtilization: number | null;
    denominator: string;
  };
  productivity: Array<{ activityDescription: string; rate: number | null; status: string; uom: string }>;
  reconciliation: { state: string; delta: number | null; message: string };
}

export interface PmvFleetTodayRow {
  profileId: string;
  assetCode: string | null;
  assetName: string | null;
  allocationId: string;
  allocationStatus: string;
  logId: string | null;
  logStatus: string | null;
  logState: string;
  workingMinutes: number;
  idleMinutes: number;
  standbyMinutes: number;
  breakdownMinutes: number;
  workingUtilization: number | null;
  attention: string[];
}

export interface PmvProfileUtilization {
  logCount: number;
  aggregate: {
    workingUtilization: number | null;
    scheduledUtilization?: number | null;
    denominator?: string;
  };
}

export interface PmvDailyOperationsSummary {
  operatingDate: string;
  deployedUnits: number;
  logsCertified: number;
  logsSubmitted: number;
  missingLogs: number;
  workingHours: number;
  averageWorkingUtilization: number | null;
  utilizationMethod?: string;
  attention: string[];
  fleet?: PmvFleetTodayRow[];
}

export interface PmvMeterReading {
  id: string;
  reading_value: number;
  meter_type: string;
  meter_unit: string;
  reading_at: string;
}

export interface PmvInspectionTemplate {
  id: string;
  template_code: string;
  template_name: string;
  inspection_kind: string;
  classification_id?: string | null;
  frequency_days?: number | null;
  checklist_items_json: Array<{ code?: string; label?: string; mandatory?: boolean }>;
}

export interface PmvInspectionResult {
  id: string;
  inspection_number: string;
  pmv_profile_id: string;
  template_id: string;
  inspection_kind: string;
  overall_result: string;
  status: string;
  inspected_at: string;
  valid_until?: string | null;
  fail_action_taken?: string | null;
  remarks?: string | null;
}

export interface PmvInspectionResultItem {
  id: string;
  item_code: string;
  item_label: string;
  item_result: string;
  is_mandatory: boolean;
  notes?: string | null;
}

export interface RecordInspectionPayload {
  template_id: string;
  items: Array<{
    item_code: string;
    item_label: string;
    item_result: 'PASS' | 'FAIL' | 'DEFECT' | 'NA';
    is_mandatory?: boolean;
    severity?: string | null;
    notes?: string | null;
  }>;
  allocation_id?: string | null;
  project_id?: string | null;
  location_description?: string | null;
  remarks?: string | null;
}

export interface PmvDocumentRequirement {
  id: string;
  requirement_code: string;
  label: string;
  document_category: string;
  classification_id?: string | null;
  document_entity_type?: string | null;
  is_mandatory: boolean;
  blocks_deployment: boolean;
}

export interface PmvBreakdownEvent {
  id: string;
  event_number: string;
  pmv_profile_id: string;
  status: string;
  severity: string;
  title: string;
  description?: string | null;
  occurred_at: string;
  resolved_at?: string | null;
  maintenance_case_id?: string | null;
  allocation_id?: string | null;
  project_id?: string | null;
}

export interface ReportBreakdownPayload {
  title: string;
  description?: string | null;
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  allocation_id?: string | null;
  project_id?: string | null;
  daily_log_id?: string | null;
  escalate_to_maintenance?: boolean;
}

export interface PmvFuelAttribution {
  id: string;
  attribution_number: string;
  pmv_profile_id: string;
  fuel_type: string;
  quantity_liters: number;
  quantity_uom?: string | null;
  quantity_litres_normalized?: number | null;
  total_cost?: number | null;
  hour_meter_reading?: number | null;
  odometer_reading?: number | null;
  source: string;
  source_type?: string | null;
  source_reference?: string | null;
  material_issuance_id?: string | null;
  material_issuance_item_id?: string | null;
  lifecycle_status?: string | null;
  quantity_authority?: string;
  quantity_authority_code?: 'INVENTORY' | 'OPERATIONAL_ONLY';
  attributed_at: string;
  remarks?: string | null;
  project_id?: string | null;
  allocation_id?: string | null;
}

export interface RecordFuelPayload {
  fuel_type?: 'DIESEL' | 'PETROL' | 'OTHER';
  quantity_liters: number;
  quantity_uom?: string | null;
  unit_cost?: number | null;
  hour_meter_reading?: number | null;
  odometer_reading?: number | null;
  allocation_id?: string | null;
  project_id?: string | null;
  material_issuance_id?: string | null;
  material_issuance_item_id?: string | null;
  source_type?: string | null;
  source_reference?: string | null;
  fuel_source_id?: string | null;
  idempotency_key?: string | null;
  remarks?: string | null;
}

export interface PmvFuelSummaryResponse {
  summary: {
    totalLiters: number;
    totalCost: number | null;
    attributionCount: number;
    primaryKpi: { label: string; value: number | null; unit: string } | null;
    denominatorNote: string | null;
  };
  attributions: PmvFuelAttribution[];
}

export interface PmvOperatorAssignment {
  id: string;
  assignment_number: string;
  pmv_profile_id: string;
  employee_id: string;
  assignment_role: string;
  status: string;
  assigned_from: string;
  assigned_until?: string | null;
  ended_at?: string | null;
  remarks?: string | null;
}

export interface AssignOperatorPayload {
  employee_id: string;
  assignment_role?: 'OPERATOR' | 'DRIVER' | 'SPOTTER';
  allocation_id?: string | null;
  project_id?: string | null;
  remarks?: string | null;
}

export interface OperatorEligibilityResult {
  eligible: boolean;
  blockers: string[];
  warnings: string[];
  checks: Array<{ requirement_code: string; label: string; status: string; message?: string }>;
}

export interface PmvProjectCost {
  id: string;
  project_id: string;
  cost_date: string;
  cost_category: string;
  total_cost: number;
  pmv_cost_kind?: string | null;
  source_type?: string | null;
  source_document_number?: string | null;
  description?: string | null;
  gl_posted?: boolean;
}

export interface PmvCostSummary {
  totalActual: number;
  byKind: Array<{ kind: string; total: number; count: number }>;
  currency: string;
}

export interface PmvBudgetCompare {
  projectId: string;
  pmvProfileId: string;
  originalBudgetEquipment: number | null;
  pmvActualTotal: number;
  variance: number | null;
  hasOriginalBudget: boolean;
  note: string | null;
}

export interface PmvControlTowerSnapshot {
  asOf: string;
  operatingDate: string;
  fleet_total: number;
  operating: number;
  available: number;
  allocated: number;
  idle: number;
  standby: number;
  under_maintenance: number;
  breakdown: number;
  quarantined: number;
  out_of_service: number;
  not_ready: number;
  compliance_expiring_30d: number;
  pm_due: number;
  pm_due_7d: number;
  open_breakdowns: number;
  utilization_pct: number | null;
  availability_pct: number | null;
  idle_pct: number | null;
  breakdown_rate_pct: number | null;
  demand: {
    openRequests: number;
    submittedRequests: number;
    reservations: number;
    inTransit: number;
    deployed: number;
  };
  daily: {
    logsExpected: number;
    logsCertified: number;
    missingLogs: number;
    workingHours: number;
    idleHours: number;
    certifiedLogCount: number;
  };
  fuel?: {
    attributionCount30d: number;
    totalLiters30d: number;
    totalCost30d: number | null;
    profilesWithFuel30d: number;
    exceptions: Array<{ pmv_profile_id: string; reason: string; detail: string }>;
  };
  projectAllocation: Array<{
    project_id: string;
    allocated_count: number;
    idle_hours?: number | null;
    fuel_liters?: number | null;
    operational_cost?: number | null;
  }>;
  attention: string[];
  signalsPublished: number;
  notes: string[];
}

export interface PmvInternalHireRate {
  id: string;
  rate_code: string;
  label: string;
  classification_id?: string | null;
  pmv_profile_id?: string | null;
  rate_per_hour: number;
  rate_per_day?: number | null;
  currency: string;
  effective_from: string;
  effective_until?: string | null;
  is_active: boolean;
}

export default pmvService;
